import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { SuiContainer, SuiTextInput, SuiDropdown, SuiDropdownOption, SuiRadioGroup, SuiRadio } from '@splashthat/sui';

import { getQuestionFormNodesOfInputType, getQuestionFormNodes } from 'common/entities/formNode/selectors';
import { INPUT_TYPE_DATE, INPUT_TYPE_TIME } from 'common/entities/formNode/inputTypes';
import {
    ENV_PRODUCTION,
    ENV_BETA,
    ENV_STAGING,
    URL_PRODUCTION,
    URL_BETA,
    URL_STAGING,
} from 'common/entities/formNode/eventOpsConstants';

import RichTextLabelControl from '../components/RichTextLabelControl';
import EditViewSectionContainer from '../EditViewSectionContainer';
import RequiredControl from '../components/RequiredControl';
import { updateFormNodeConfigValue } from '../../../actions/nodeActions';
import { getEditFormNodeId, getEditFormNode, getFormId } from '../../../selectors';

const LABEL_USERNAME = 'Username';
const LABEL_PASSWORD = 'Password';
const LABEL_CLIENT_ID = 'Client Id';
const LABEL_CLIENT_SECRET = 'Client Secret';
const LABEL_START_DATE_QUESTION = 'Start Date Question';
const LABEL_START_TIME_QUESTION = 'Start Time Question';
const LABEL_END_DATE_QUESTION = 'End Date Question';
const LABEL_END_TIME_QUESTION = 'End Time Question';
const LABEL_TIME_ZONE_QUESTION = 'Time Zone Question';
const LABEL_WIDGET_ENV = 'Widget Server';
const HELPER_TEXT_WIDGET_ENV = 'Save and refresh required';
const LABEL_SERVER_PRODUCTION = 'Production';
const LABEL_SERVER_BETA = 'Beta';
const LABEL_SERVER_STAGING = 'Staging';

const CONFIG_NAME_EVENT_OPS_USERNAME = 'eventOpsUsername';
const CONFIG_NAME_EVENT_OPS_PASSWORD = 'eventOpsPassword';
const CONFIG_NAME_EVENT_OPS_CLIENT_ID = 'eventOpsClientId';
const CONFIG_NAME_EVENT_OPS_CLIENT_SECRET = 'eventOpsClientSecret';
const CONFIG_NAME_START_DATE_NODE_CODE = 'eventOpsStartDateNodeCode';
const CONFIG_NAME_START_TIME_NODE_CODE = 'eventOpsStartTimeNodeCode';
const CONFIG_NAME_END_DATE_NODE_CODE = 'eventOpsEndDateNodeCode';
const CONFIG_NAME_END_TIME_NODE_CODE = 'eventOpsEndTimeNodeCode';
const CONFIG_NAME_TIME_ZONE_NODE_CODE = 'eventOpsTimeZoneNodeCode';
const CONFIG_NAME_WIDGET_ENV = 'eventOpsWidgetEnv';

const renderQuestionsForDropdown = (nodes) =>
    nodes.map(({ id, code, label }) => <SuiDropdownOption value={code} key={id} displayValue={label} />);

/**
 * Component for main guest information fields like first name, last name and email.
 */
const EventOpsEditView = (props) => (
    <React.Fragment>
        <RequiredControl />
        <EditViewSectionContainer>
            <SuiContainer variation="flex-group">
                <RichTextLabelControl formNodeId={props.formNodeId} />
            </SuiContainer>

            <SuiContainer variation="flex-group">
                <SuiTextInput
                    value={props.eventOpsUsername}
                    fullWidth
                    label={LABEL_USERNAME}
                    onChange={(e, value) =>
                        props.updateFormNodeConfigValue(props.formNodeId, CONFIG_NAME_EVENT_OPS_USERNAME, value)
                    }
                />
            </SuiContainer>
            <SuiContainer variation="flex-group">
                <SuiTextInput
                    value={props.eventOpsPassword}
                    fullWidth
                    type="password"
                    label={LABEL_PASSWORD}
                    onChange={(e, value) =>
                        props.updateFormNodeConfigValue(props.formNodeId, CONFIG_NAME_EVENT_OPS_PASSWORD, value)
                    }
                />
            </SuiContainer>
            <SuiContainer variation="flex-group">
                <SuiTextInput
                    value={props.eventOpsClientId}
                    fullWidth
                    label={LABEL_CLIENT_ID}
                    onChange={(e, value) =>
                        props.updateFormNodeConfigValue(props.formNodeId, CONFIG_NAME_EVENT_OPS_CLIENT_ID, value)
                    }
                />
            </SuiContainer>
            <SuiContainer variation="flex-group">
                <SuiTextInput
                    value={props.eventOpsClientSecret}
                    fullWidth
                    label={LABEL_CLIENT_SECRET}
                    onChange={(e, value) =>
                        props.updateFormNodeConfigValue(props.formNodeId, CONFIG_NAME_EVENT_OPS_CLIENT_SECRET, value)
                    }
                />
            </SuiContainer>
            <SuiContainer variation="flex-group">
                <SuiDropdown
                    placeholder={props.dateQuestionNodes.length ? '-' : 'No date questions'}
                    value={props.eventOpsStartDateNodeCode}
                    fullWidth
                    label={LABEL_START_DATE_QUESTION}
                    onChange={(e, value) =>
                        props.updateFormNodeConfigValue(props.formNodeId, CONFIG_NAME_START_DATE_NODE_CODE, value)
                    }
                >
                    {renderQuestionsForDropdown(props.dateQuestionNodes)}
                </SuiDropdown>
            </SuiContainer>
            <SuiContainer variation="flex-group">
                <SuiDropdown
                    placeholder={props.timeQuestionNodes.length ? '-' : 'No time questions'}
                    value={props.eventOpsStartTimeNodeCode}
                    fullWidth
                    label={LABEL_START_TIME_QUESTION}
                    onChange={(e, value) =>
                        props.updateFormNodeConfigValue(props.formNodeId, CONFIG_NAME_START_TIME_NODE_CODE, value)
                    }
                >
                    {renderQuestionsForDropdown(props.timeQuestionNodes)}
                </SuiDropdown>
            </SuiContainer>
            <SuiContainer variation="flex-group">
                <SuiDropdown
                    placeholder={props.dateQuestionNodes.length ? '-' : 'No date questions'}
                    value={props.eventOpsEndDateNodeCode}
                    fullWidth
                    label={LABEL_END_DATE_QUESTION}
                    onChange={(e, value) =>
                        props.updateFormNodeConfigValue(props.formNodeId, CONFIG_NAME_END_DATE_NODE_CODE, value)
                    }
                >
                    {renderQuestionsForDropdown(props.dateQuestionNodes)}
                </SuiDropdown>
            </SuiContainer>
            <SuiContainer variation="flex-group">
                <SuiDropdown
                    placeholder={props.timeQuestionNodes.length ? '-' : 'No time questions'}
                    value={props.eventOpsEndTimeNodeCode}
                    fullWidth
                    label={LABEL_END_TIME_QUESTION}
                    onChange={(e, value) =>
                        props.updateFormNodeConfigValue(props.formNodeId, CONFIG_NAME_END_TIME_NODE_CODE, value)
                    }
                >
                    {renderQuestionsForDropdown(props.timeQuestionNodes)}
                </SuiDropdown>
            </SuiContainer>
            <SuiContainer variation="flex-group">
                <SuiDropdown
                    placeholder={props.allQuestionNodes.length ? '-' : 'No questions'}
                    value={props.eventOpsTimeZoneNodeCode}
                    fullWidth
                    label={LABEL_TIME_ZONE_QUESTION}
                    onChange={(e, value) =>
                        props.updateFormNodeConfigValue(props.formNodeId, CONFIG_NAME_TIME_ZONE_NODE_CODE, value)
                    }
                >
                    {renderQuestionsForDropdown(props.allQuestionNodes)}
                </SuiDropdown>
            </SuiContainer>
            <SuiContainer variation="flex-group">
                <SuiRadioGroup
                    label={LABEL_WIDGET_ENV}
                    value={props.eventOpsWidgetEnv}
                    helperText={HELPER_TEXT_WIDGET_ENV}
                    defaultValue={ENV_PRODUCTION}
                    onChange={(e, value) =>
                        props.updateFormNodeConfigValue(props.formNodeId, CONFIG_NAME_WIDGET_ENV, value)
                    }
                >
                    <SuiRadio label={LABEL_SERVER_PRODUCTION} value={ENV_PRODUCTION} helperText={URL_PRODUCTION} />
                    <SuiRadio label={LABEL_SERVER_BETA} value={ENV_BETA} helperText={URL_BETA} />
                    <SuiRadio label={LABEL_SERVER_STAGING} value={ENV_STAGING} helperText={URL_STAGING} />
                </SuiRadioGroup>
            </SuiContainer>
        </EditViewSectionContainer>
    </React.Fragment>
);

const propTypeQuestionNode = PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.label,
});

EventOpsEditView.propTypes = {
    formNodeId: PropTypes.string.isRequired,
    updateFormNodeConfigValue: PropTypes.func.isRequired,
    eventOpsUsername: PropTypes.string,
    eventOpsPassword: PropTypes.string,
    eventOpsClientId: PropTypes.string,
    eventOpsClientSecret: PropTypes.string,
    eventOpsStartDateNodeCode: PropTypes.string,
    eventOpsStartTimeNodeCode: PropTypes.string,
    eventOpsEndDateNodeCode: PropTypes.string,
    eventOpsEndTimeNodeCode: PropTypes.string,
    eventOpsTimeZoneNodeCode: PropTypes.string,
    eventOpsWidgetEnv: PropTypes.string,
    dateQuestionNodes: PropTypes.arrayOf(propTypeQuestionNode).isRequired,
    timeQuestionNodes: PropTypes.arrayOf(propTypeQuestionNode).isRequired,
    allQuestionNodes: PropTypes.arrayOf(propTypeQuestionNode).isRequired,
};

EventOpsEditView.defaultProps = {
    eventOpsUsername: '',
    eventOpsPassword: '',
    eventOpsClientId: '',
    eventOpsClientSecret: '',
    eventOpsStartDateNodeCode: undefined,
    eventOpsStartTimeNodeCode: undefined,
    eventOpsEndDateNodeCode: undefined,
    eventOpsEndTimeNodeCode: undefined,
    eventOpsTimeZoneNodeCode: undefined,
    eventOpsWidgetEnv: undefined,
};

const mapStateToProps = (state) => {
    const formId = getFormId(state);
    const node = getEditFormNode(state);
    const formNodeId = getEditFormNodeId(state);

    return {
        formNodeId,
        name: node ? node.name : undefined,
        eventOpsUsername: node.config[CONFIG_NAME_EVENT_OPS_USERNAME],
        eventOpsPassword: node.config[CONFIG_NAME_EVENT_OPS_PASSWORD],
        eventOpsClientId: node.config[CONFIG_NAME_EVENT_OPS_CLIENT_ID],
        eventOpsClientSecret: node.config[CONFIG_NAME_EVENT_OPS_CLIENT_SECRET],
        eventOpsStartDateNodeCode: node.config[CONFIG_NAME_START_DATE_NODE_CODE],
        eventOpsStartTimeNodeCode: node.config[CONFIG_NAME_START_TIME_NODE_CODE],
        eventOpsEndDateNodeCode: node.config[CONFIG_NAME_END_DATE_NODE_CODE],
        eventOpsEndTimeNodeCode: node.config[CONFIG_NAME_END_TIME_NODE_CODE],
        eventOpsTimeZoneNodeCode: node.config[CONFIG_NAME_TIME_ZONE_NODE_CODE],
        eventOpsWidgetEnv: node.config[CONFIG_NAME_WIDGET_ENV],
        dateQuestionNodes: getQuestionFormNodesOfInputType(state, formId, INPUT_TYPE_DATE),
        timeQuestionNodes: getQuestionFormNodesOfInputType(state, formId, INPUT_TYPE_TIME),
        allQuestionNodes: getQuestionFormNodes(state, formId),
    };
};

const mapDispatchToProps = {
    updateFormNodeConfigValue,
};

export default connect(mapStateToProps, mapDispatchToProps)(EventOpsEditView);
